import { useEffect, useState } from 'react';
import { useI18n } from '../i18n';
import { OverviewSummary, api } from '../lib/api';

type Props = {
  onNavigate: (page: 'live' | 'playback' | 'streams') => void;
  canManageStreams: boolean;
};

type SummaryTone = 'cyan' | 'green' | 'violet' | 'amber' | 'blue' | 'rose';

function SummaryCard({
  label,
  value,
  detail,
  tone,
  eyebrow,
}: {
  label: string;
  value?: number;
  detail: string;
  tone: SummaryTone;
  eyebrow: string;
}) {
  return (
    <article className={`overview-card overview-card-${tone}`}>
      <div className="overview-card-heading">
        <span>{label}</span>
        <em>{eyebrow}</em>
      </div>
      <strong>{value ?? '—'}</strong>
      <small>{detail}</small>
      <i aria-hidden="true" />
    </article>
  );
}

function QuickAccessCard({
  label,
  badge,
  onClick,
  primary,
}: {
  label: string;
  badge: string;
  onClick: () => void;
  primary?: boolean;
}) {
  return (
    <button className={`overview-quick-card ${primary ? 'primary' : ''}`} onClick={onClick}>
      <span className="overview-quick-badge">{badge}</span>
      <span className="overview-quick-copy"><strong>{label}</strong></span>
      <span className="overview-quick-arrow" aria-hidden="true">→</span>
    </button>
  );
}

export function DashboardPage({ onNavigate, canManageStreams }: Props) {
  const { t, locale } = useI18n();
  const [summary, setSummary] = useState<OverviewSummary>();
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const result = await api<OverviewSummary>('/overview/summary');
        if (active) {
          setSummary(result);
          setError('');
        }
      } catch (reason) {
        if (active) setError(reason instanceof Error ? reason.message : t('overview.loadFailed'));
      }
    };
    void load();
    const timer = window.setInterval(() => void load(), 10_000);
    return () => { active = false; window.clearInterval(timer); };
  }, [t]);

  return (
    <section className="page-section dashboard-page">
      <header className="page-header dashboard-header">
        <div>
          <p className="eyebrow">{t('overview.eyebrow')}</p>
          <h2>{t('overview.title')}</h2>
        </div>
        <span className="muted overview-updated">
          {summary
            ? t('overview.updatedAt', { time: new Date(summary.generatedAt).toLocaleTimeString(locale) })
            : t('overview.reading')}
        </span>
      </header>
      {error && <div className="notice error-notice">{error}</div>}

      <section className="overview-hero-panel">
        <div className="overview-brand-block">
          <div className="overview-logo-shell">
            <img src="/oa-plaza-logo.png" alt={t('app.company')} />
          </div>
          <div>
            <span>{t('app.company')}</span>
            <h3>{t('overview.brandTitle')}</h3>
          </div>
        </div>

        <div className="overview-quick-grid">
          <QuickAccessCard label={t('overview.live')} badge="LIVE" primary onClick={() => onNavigate('live')} />
          <QuickAccessCard label={t('overview.playback')} badge="NVR" onClick={() => onNavigate('playback')} />
          {canManageStreams && (
            <QuickAccessCard label={t('overview.streams')} badge="CAT" onClick={() => onNavigate('streams')} />
          )}
        </div>
      </section>

      <div className="overview-grid overview-grid-polished">
        <SummaryCard label={t('overview.videoSources')} value={summary?.totalVideoSources} detail={t('overview.onlineCount', { count: summary?.onlineVideoSources ?? 0 })} tone="cyan" eyebrow="CATALOG" />
        <SummaryCard label={t('overview.offlineSources')} value={summary?.offlineVideoSources} detail={t('overview.offlineDetail')} tone="rose" eyebrow="OFFLINE" />
        <SummaryCard label={t('overview.disabledSources')} value={summary?.disabledVideoSources} detail={t('overview.disabledDetail')} tone="violet" eyebrow="DISABLED" />
        <SummaryCard label={t('overview.activeUpstreams')} value={summary?.activeUpstreamStreams} detail={t('overview.upstreamDetail')} tone="green" eyebrow="UPSTREAM" />
        <SummaryCard label={t('overview.liveSessions')} value={summary?.liveViewerSessions} detail={t('overview.activeUsers', { count: summary?.activeUsers ?? 0 })} tone="blue" eyebrow="LIVE" />
        <SummaryCard label={t('overview.playbackSessions')} value={summary?.playbackSessions} detail={t('overview.playbackDetail')} tone="amber" eyebrow="PLAYBACK" />
      </div>

      {(summary?.latestMediaError || summary?.latestSystemError) && (
        <section className="overview-errors overview-errors-polished">
          <div><p className="eyebrow">{t('overview.recentEvents')}</p><h3>{t('overview.recentErrors')}</h3></div>
          <div className="overview-error-list">
            {summary.latestMediaError && <p><strong>{t('overview.media')}</strong><span>{summary.latestMediaError}</span></p>}
            {summary.latestSystemError && <p><strong>{t('overview.system')}</strong><span>{summary.latestSystemError}</span></p>}
          </div>
        </section>
      )}
    </section>
  );
}
